import { ref, get, update, set } from 'firebase/database';
import { database } from '../firebase/config';
import { Empresa, ClienteResumo, Assinatura, OnboardingData, DashboardMetrics, GatewayConfig, PagamentoEscola, RecursoContratado, SubcontaAsaasInfo, AmbienteGateway } from '../types';
import { CATALOGO_GATEWAYS, urlWebhook } from '../data/gateways';
import type { NovaEscolaForm } from '../components/NovaEscolaModal';

class MasterFirebaseService {

  async getTodasEmpresas(): Promise<Empresa[]> {
    const snapshot = await get(ref(database, 'empresas'));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const empresas: Empresa[] = [];

    Object.keys(data).forEach((cnpj) => {
      const emp = data[cnpj];
      if (emp && emp.nomeFantasia) {
        empresas.push({
          cnpj,
          nomeFantasia: emp.nomeFantasia || '',
          razaoSocial: emp.razaoSocial || '',
          email: emp.email || '',
          telefone: emp.telefone || '',
          site: emp.site || '',
          status: emp.status !== false,
          createDate: emp.createDate || '',
          updateDate: emp.updateDate || '',
          deleteDate: emp.deleteDate || '',
          inscricaoEstadual: emp.inscricaoEstadual || '',
          numero: emp.numero || '',
          complemento: emp.complemento || '',
          bairro: emp.bairro || '',
          cidade: emp.cidade || '',
          logradouro: emp.logradouro || '',
          cep: emp.cep || '',
          googlePlaceId: emp.googlePlaceId || '',
          logoUrl: emp.logoUrl || '',
          plano: emp.plano || undefined,
          asaasConfig: emp.asaasConfig || undefined,
        });
      }
    });

    return empresas;
  }

  async getClientesResumo(): Promise<ClienteResumo[]> {
    const [empresasSnap, assinaturasSnap, mensalidadesSnap] = await Promise.all([
      get(ref(database, 'empresas')),
      get(ref(database, 'assinaturas')),
      get(ref(database, 'mensalidades')),
    ]);

    if (!empresasSnap.exists()) return [];

    const data = empresasSnap.val();
    const assinaturasRaiz = assinaturasSnap.exists() ? assinaturasSnap.val() : {};
    const mensalidadesRaiz = mensalidadesSnap.exists() ? mensalidadesSnap.val() : {};
    const clientes: ClienteResumo[] = [];

    for (const cnpj of Object.keys(data)) {
      const emp = data[cnpj];
      if (!emp || !emp.nomeFantasia) continue;

      const usuarios = emp.usuarios ? Object.keys(emp.usuarios).length : 0;
      const alunos = emp.alunos ? Object.keys(emp.alunos).filter(k => {
        const a = emp.alunos[k];
        return a && a.bAtivo !== false;
      }).length : 0;

      const temAssinaturaAtiva = this.verificarAssinaturaAtiva(cnpj, assinaturasRaiz);
      const temPlanoAtivo = emp.plano && emp.plano.status === 'ativo';

      let statusFinanceiro: 'em_dia' | 'atraso' | 'sem_assinatura' = 'sem_assinatura';

      if (temAssinaturaAtiva || temPlanoAtivo) {
        statusFinanceiro = 'em_dia';

        if (this.verificarAtraso(cnpj, assinaturasRaiz, mensalidadesRaiz)) {
          statusFinanceiro = 'atraso';
        }
      }

      let planoValor = emp.plano?.valor || 0;
      if (!planoValor && assinaturasRaiz[cnpj]) {
        const subs = Object.values(assinaturasRaiz[cnpj]) as any[];
        const ativa = subs.find(a => a.status === 'ACTIVE');
        if (ativa) planoValor = ativa.valor ?? ativa.value ?? 0;
      }

      clientes.push({
        cnpj,
        nomeFantasia: emp.nomeFantasia || '',
        email: emp.email || '',
        telefone: emp.telefone || '',
        status: emp.status !== false,
        createDate: emp.createDate || '',
        planoNome: emp.plano?.nome || (temAssinaturaAtiva ? 'Assinatura ativa' : 'Sem plano'),
        planoValor,
        planoStatus: emp.plano?.status || (temAssinaturaAtiva ? 'ativo' : 'inativo'),
        totalAlunos: alunos,
        totalUsuarios: usuarios,
        statusFinanceiro,
      });
    }

    return clientes.sort((a, b) => a.nomeFantasia.localeCompare(b.nomeFantasia));
  }

  private verificarAssinaturaAtiva(cnpj: string, assinaturasRaiz: any): boolean {
    if (assinaturasRaiz[cnpj]) {
      const subs = Object.values(assinaturasRaiz[cnpj]) as any[];
      if (subs.some(a => a.status === 'ACTIVE')) return true;
    }
    return false;
  }

  private verificarAtraso(cnpj: string, assinaturasRaiz: any, mensalidadesRaiz: any): boolean {
    if (assinaturasRaiz[cnpj]) {
      const subs = Object.values(assinaturasRaiz[cnpj]) as any[];
      if (subs.some(a => a.status === 'OVERDUE' || a.status === 'PAST_DUE')) return true;
    }

    if (mensalidadesRaiz[cnpj]) {
      const hoje = new Date();
      const empresaMensalidades = mensalidadesRaiz[cnpj];
      for (const chave of Object.keys(empresaMensalidades)) {
        const item = empresaMensalidades[chave];
        if (item && typeof item === 'object') {
          const entries = Object.values(item) as any[];
          if (entries.some(m => {
            if (m.status === 'pendente' || m.status === 'vencida' || m.status === 'PENDING' || m.status === 'OVERDUE') {
              const venc = m.dataVencimento || m.dueDate;
              return venc && new Date(venc) < hoje;
            }
            return false;
          })) return true;
        }
      }
    }

    return false;
  }

  async getEmpresaDetalhes(cnpj: string): Promise<Empresa | null> {
    const snapshot = await get(ref(database, `empresas/${cnpj}`));
    if (!snapshot.exists()) return null;

    const emp = snapshot.val();
    return {
      cnpj,
      nomeFantasia: emp.nomeFantasia || '',
      razaoSocial: emp.razaoSocial || '',
      email: emp.email || '',
      telefone: emp.telefone || '',
      site: emp.site || '',
      status: emp.status !== false,
      createDate: emp.createDate || '',
      updateDate: emp.updateDate || '',
      deleteDate: emp.deleteDate || '',
      inscricaoEstadual: emp.inscricaoEstadual || '',
      numero: emp.numero || '',
      complemento: emp.complemento || '',
      bairro: emp.bairro || '',
      cidade: emp.cidade || '',
      logradouro: emp.logradouro || '',
      cep: emp.cep || '',
      googlePlaceId: emp.googlePlaceId || '',
      logoUrl: emp.logoUrl || '',
      plano: emp.plano || undefined,
      asaasConfig: emp.asaasConfig || undefined,
    };
  }

  async getAssinaturasEmpresa(cnpj: string): Promise<Assinatura[]> {
    const snapshot = await get(ref(database, `assinaturas/${cnpj}`));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const resultado: Assinatura[] = [];

    Object.keys(data).forEach(key => {
      const a = data[key];
      if (!a) return;
      resultado.push({
        id: key,
        asaasSubscriptionId: a.asaasSubscriptionId || '',
        customerId: a.customerId || '',
        status: a.status || '',
        cycle: a.ciclo || a.cycle || '',
        value: a.valor ?? a.value ?? 0,
        description: a.descricao || a.description || '',
        nextDueDate: a.proximoVencimento || a.nextDueDate || '',
        createDate: a.dataCriacao || a.createDate || '',
        updateDate: a.dataAtualizacao || a.updateDate || '',
        empresaId: a.empresaId || cnpj,
        codigo: a.codigo || '',
        dataInicio: a.dataInicio || '',
        origem: 'legacy',
      });
    });

    return resultado;
  }

  async getContagens(cnpj: string): Promise<{ alunos: number; alunosAtivos: number; usuarios: number }> {
    const [alunosSnap, usuariosSnap] = await Promise.all([
      get(ref(database, `empresas/${cnpj}/alunos`)),
      get(ref(database, `empresas/${cnpj}/usuarios`)),
    ]);

    let alunos = 0;
    let alunosAtivos = 0;
    if (alunosSnap.exists()) {
      const data = alunosSnap.val();
      Object.values(data).forEach((a: any) => {
        alunos++;
        if (a.bAtivo !== false) alunosAtivos++;
      });
    }

    const usuarios = usuariosSnap.exists() ? Object.keys(usuariosSnap.val()).length : 0;

    return { alunos, alunosAtivos, usuarios };
  }

  async getMensalidadesEmpresa(cnpj: string): Promise<any[]> {
    const snapshot = await get(ref(database, `mensalidades/${cnpj}`));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const mensalidades: any[] = [];
    this.extrairMensalidades(data, mensalidades, '');
    return mensalidades;
  }

  private extrairMensalidades(node: any, resultado: any[], path: string): void {
    if (!node || typeof node !== 'object') return;

    if (node.valor !== undefined || node.value !== undefined || node.dataVencimento || node.dueDate || node.status) {
      resultado.push({
        ...node,
        id: node.id || path,
        grupoId: path,
      });
      return;
    }

    Object.keys(node).forEach(key => {
      this.extrairMensalidades(node[key], resultado, path ? `${path}/${key}` : key);
    });
  }

  async criarEmpresa(dados: NovaEscolaForm): Promise<string> {
    const cnpj = String(dados.cnpj || '').replace(/\D/g, '');
    if (cnpj.length !== 14) {
      throw new Error('Informe um CNPJ válido com 14 dígitos.');
    }

    const existente = await get(ref(database, `empresas/${cnpj}`));
    if (existente.exists() && existente.val()?.nomeFantasia) {
      throw new Error('Já existe uma escola cadastrada com este CNPJ.');
    }

    const agora = new Date().toISOString();
    const empresa = {
      id: cnpj,
      cnpj,
      createDate: agora,
      updateDate: agora,
      deleteDate: '',
      email: dados.email.trim(),
      telefone: dados.telefone.trim(),
      site: dados.site.trim(),
      status: true,
      nomeFantasia: dados.nomeFantasia.trim(),
      razaoSocial: dados.razaoSocial.trim(),
      inscricaoEstadual: 0,
      numero: dados.numero.trim(),
      complemento: dados.complemento.trim(),
      bairro: dados.bairro.trim(),
      cidade: dados.cidade.trim(),
      logradouro: dados.logradouro.trim(),
      cep: String(dados.cep || '').replace(/\D/g, ''),
      googlePlaceId: '',
      logoUrl: '',
    };

    await set(ref(database, `empresas/${cnpj}`), empresa);
    await update(ref(database, `empresas/${cnpj}/recursos/whatsapp`), {
      ativo: true,
      atualizadoEm: agora,
    });

    return cnpj;
  }

  async criarGestorEscola(params: {
    cnpj: string;
    nome: string;
    email: string;
    telefone?: string;
  }): Promise<{ email: string; senhaTemporaria: string; nome: string; uid: string }> {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../firebase/config');
    const chamar = httpsCallable<
      { empresaId: string; nome: string; email: string; telefone?: string },
      { email: string; senhaTemporaria: string; nome: string; uid: string }
    >(functions, 'criarGestorEscola');

    const resultado = await chamar({
      empresaId: params.cnpj.replace(/\D/g, ''),
      nome: params.nome.trim(),
      email: params.email.trim(),
      telefone: params.telefone?.trim() || '',
    });

    return resultado.data;
  }

  async garantirAdminVictall(cnpj: string): Promise<{
    email: string;
    nome: string;
    uid: string;
    jaExistia: boolean;
    criadoAuth: boolean;
    senhaTemporaria: string;
    mensagem: string;
  }> {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../firebase/config');
    const chamar = httpsCallable<
      { empresaId: string },
      {
        email: string;
        nome: string;
        uid: string;
        jaExistia: boolean;
        criadoAuth: boolean;
        senhaTemporaria: string;
        mensagem: string;
      }
    >(functions, 'garantirAdminVictall');

    const resultado = await chamar({
      empresaId: cnpj.replace(/\D/g, ''),
    });

    return resultado.data;
  }

  async sincronizarAssinaturaEscola(cnpj: string): Promise<{
    ok: boolean;
    customerId: string;
    ambiente: string;
    assinaturas: number;
    mensalidades: number;
  }> {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../firebase/config');
    const chamar = httpsCallable<
      { empresaId: string },
      { ok: boolean; customerId: string; ambiente: string; assinaturas: number; mensalidades: number }
    >(functions, 'sincronizarAssinaturaEscola');

    const resultado = await chamar({
      empresaId: cnpj.replace(/\D/g, ''),
    });

    return resultado.data;
  }

  async getUsuariosPainel(cnpj: string): Promise<Array<{
    id: string;
    nome: string;
    email: string;
    cargo: string;
    bAtivo: boolean;
    origem: string;
  }>> {
    const snapshot = await get(ref(database, `empresas/${cnpj}/usuarios`));
    if (!snapshot.exists()) return [];

    const dados = snapshot.val() || {};
    return Object.entries(dados)
      .map(([id, raw]: [string, any]) => ({
        id,
        nome: raw?.nome || '',
        email: raw?.email || '',
        cargo: raw?.cargo || '',
        bAtivo: raw?.bAtivo === true || raw?.bAtivo === 1,
        origem: raw?.origem || '',
      }))
      .filter((u) => {
        const cargo = (u.cargo || '').toLowerCase();
        return cargo === 'administrador' || cargo === 'admin' || cargo === 'diretoria' || cargo === 'diretor' || cargo === 'gestor' || cargo === 'financeiro';
      })
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }

  async alterarStatusEmpresa(cnpj: string, novoStatus: boolean): Promise<void> {
    await update(ref(database, `empresas/${cnpj}`), {
      status: novoStatus,
      updateDate: new Date().toISOString(),
    });
  }

  async getRecurso(cnpj: string, codigo: string): Promise<RecursoContratado> {
    const snapshot = await get(ref(database, `empresas/${cnpj}/recursos/${codigo}`));
    if (!snapshot.exists()) return { ativo: true, definido: false };
    const valor = snapshot.val() || {};
    return {
      ativo: valor.ativo === true,
      definido: true,
      atualizadoEm: valor.atualizadoEm || '',
    };
  }

  async salvarRecurso(cnpj: string, codigo: string, ativo: boolean): Promise<void> {
    await update(ref(database, `empresas/${cnpj}/recursos/${codigo}`), {
      ativo,
      atualizadoEm: new Date().toISOString(),
    });
  }

  private normalizarGateways(data: Record<string, any>): Record<string, GatewayConfig> {
    const resultado: Record<string, GatewayConfig> = {};
    Object.keys(data || {}).forEach((id) => {
      const item = data[id] || {};
      resultado[id] = {
        ativo: item.ativo === true,
        ambienteAtivo: item.ambienteAtivo === 'producao' ? 'producao' : 'homologacao',
        homologacao: item.homologacao && typeof item.homologacao === 'object' ? item.homologacao : {},
        producao: item.producao && typeof item.producao === 'object' ? item.producao : {},
        atualizadoEm: item.atualizadoEm || '',
      };
    });
    return resultado;
  }

  async getGateways(cnpj: string): Promise<Record<string, GatewayConfig>> {
    const privado = await get(ref(database, `plataforma/credenciais/${cnpj}/gateways`));
    if (privado.exists()) return this.normalizarGateways(privado.val());

    const antigo = await get(ref(database, `empresas/${cnpj}/gateways`));
    if (!antigo.exists()) return {};
    return this.normalizarGateways(antigo.val());
  }

  async getSubcontaAsaas(cnpj: string, ambiente: AmbienteGateway): Promise<SubcontaAsaasInfo | null> {
    const snap = await get(ref(database, `plataforma/credenciais/${cnpj}/gateways/asaas/${ambiente}`));
    if (!snap.exists()) return null;
    const dados = snap.val() || {};
    return {
      modo: dados.modo || (dados.token ? 'token_manual' : ''),
      accountId: dados.accountId || '',
      walletId: dados.walletId || '',
      statusOnboarding: dados.statusOnboarding || '',
      statusDetalhe: dados.statusDetalhe || undefined,
      onboardingUrls: Array.isArray(dados.onboardingUrls) ? dados.onboardingUrls : [],
      updateDate: dados.updateDate || '',
    };
  }

  async salvarGateway(cnpj: string, id: string, config: GatewayConfig): Promise<void> {
    const atualizadoEm = new Date().toISOString();
    const atuais = await this.getGateways(cnpj);
    const gravados: Record<string, GatewayConfig> = {
      ...atuais,
      [id]: { ...config, atualizadoEm },
    };

    if (config.ativo) {
      Object.keys(gravados).forEach((outro) => {
        if (outro !== id) gravados[outro] = { ...gravados[outro], ativo: false };
      });
    }

    const updates: Record<string, unknown> = {
      [`empresas/${cnpj}/gateways`]: null,
    };

    for (const [gatewayId, item] of Object.entries(gravados)) {
      const preservar: Record<string, unknown> = {};
      for (const ambiente of ['homologacao', 'producao'] as AmbienteGateway[]) {
        const atualSnap = await get(ref(database, `plataforma/credenciais/${cnpj}/gateways/${gatewayId}/${ambiente}`));
        const atual = atualSnap.exists() ? atualSnap.val() || {} : {};
        const form = item[ambiente] || {};
        preservar[ambiente] = {
          ...atual,
          ...form,
          // Nunca apagar metadados de subconta ao salvar o formulário de token
          modo: atual.modo || form.modo,
          accountId: atual.accountId || form.accountId,
          walletId: atual.walletId || form.walletId,
          statusOnboarding: atual.statusOnboarding || form.statusOnboarding,
          statusDetalhe: atual.statusDetalhe || form.statusDetalhe,
          onboardingUrls: atual.onboardingUrls || form.onboardingUrls,
          documentos: atual.documentos,
          createDate: atual.createDate,
          updateDate: atualizadoEm,
        };
      }

      updates[`plataforma/credenciais/${cnpj}/gateways/${gatewayId}`] = {
        ativo: item.ativo,
        ambienteAtivo: item.ambienteAtivo,
        homologacao: preservar.homologacao,
        producao: preservar.producao,
        atualizadoEm: item.atualizadoEm || atualizadoEm,
      };
    }

    const ativo = Object.entries(gravados).find(([, item]) => item.ativo);
    if (ativo) {
      const [gatewayId, item] = ativo;
      const catalogo = CATALOGO_GATEWAYS.find((entrada) => entrada.id === gatewayId);
      const pagamentoAtualSnap = await get(ref(database, `empresas/${cnpj}/pagamento`));
      const pagamentoAtual = pagamentoAtualSnap.exists() ? pagamentoAtualSnap.val() || {} : {};
      const subcontaAmbiente = await this.getSubcontaAsaas(cnpj, item.ambienteAtivo);
      const pagamento: PagamentoEscola & { modoGateway?: string; statusSubconta?: string } = {
        gatewayId,
        ambienteAtivo: item.ambienteAtivo,
        metodos: catalogo?.metodos || [],
        webhooks: {
          homologacao: urlWebhook(cnpj, gatewayId, 'homologacao'),
          producao: urlWebhook(cnpj, gatewayId, 'producao'),
        },
        atualizadoEm,
        modoGateway: subcontaAmbiente?.modo || pagamentoAtual.modoGateway || 'token_manual',
        statusSubconta: subcontaAmbiente?.statusOnboarding || pagamentoAtual.statusSubconta || '',
      };
      updates[`empresas/${cnpj}/pagamento`] = pagamento;
    } else {
      updates[`empresas/${cnpj}/pagamento`] = null;
    }

    await update(ref(database), updates);
  }

  async getOnboardingsPendentes(): Promise<OnboardingData[]> {
    const snapshot = await get(ref(database, 'onboarding'));
    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    const pendentes: OnboardingData[] = [];

    Object.keys(data).forEach(uid => {
      const ob = data[uid];
      if (ob && ob.status !== 'concluido') {
        pendentes.push({
          nome: ob.nome || '',
          email: ob.email || '',
          telefone: ob.telefone || '',
          cnpj: ob.cnpj || '',
          empresaNome: ob.empresaNome || '',
          status: ob.status || 'pendente',
          etapaAtual: ob.etapaAtual || 1,
          planoSelecionado: ob.planoSelecionado,
          planoNome: ob.planoNome,
          planoValor: ob.planoValor,
          dataCriacao: ob.dataCriacao || ob.createDate || '',
          dataUltimaAtividade: ob.dataUltimaAtividade || '',
        });
      }
    });

    return pendentes.sort((a, b) =>
      new Date(b.dataUltimaAtividade || 0).getTime() - new Date(a.dataUltimaAtividade || 0).getTime()
    );
  }

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const clientes = await this.getClientesResumo();

    const clientesAtivos = clientes.filter(c => c.status).length;
    const clientesInativos = clientes.filter(c => !c.status).length;
    const clientesEmAtraso = clientes.filter(c => c.statusFinanceiro === 'atraso').length;
    const receitaMensal = clientes
      .filter(c => c.status && c.planoStatus === 'ativo')
      .reduce((sum, c) => sum + c.planoValor, 0);

    const onboardings = await this.getOnboardingsPendentes();

    return {
      totalClientes: clientes.length,
      clientesAtivos,
      clientesInativos,
      clientesEmAtraso,
      receitaMensal,
      onboardingsPendentes: onboardings.length,
    };
  }
}

export const masterFirebaseService = new MasterFirebaseService();
